import { VariantInformation } from "./isVariantInformation";

/**
 * An overlapping pair of JSON Pointer paths belonging to different named patches.
 */
export interface VariantPatchConflict {
  firstPatchName: string;
  firstPath: string;
  secondPatchName: string;
  secondPath: string;
}

/**
 * Decode the property levels used to group a JSON Pointer path.
 *
 * @param path - RFC6901 JSON Pointer path
 * @returns The initial property, or the body property and its immediate child
 */
const getPointerTokens = (path: string): string[] =>
  path === ""
    ? []
    : path
        .slice(1)
        .split("/")
        .slice(0, path.startsWith("/body/") ? 2 : 1)
        .map((token) => token.replace(/~1/g, "/").replace(/~0/g, "~"));

/**
 * Check whether two paths affect the same initial property or immediate body child.
 *
 * @param firstPath - First RFC6901 path
 * @param secondPath - Second RFC6901 path
 * @returns True when the paths overlap
 */
const pathsOverlap = (firstPath: string, secondPath: string): boolean => {
  const firstTokens = getPointerTokens(firstPath);
  const secondTokens = getPointerTokens(secondPath);
  const sharedLength = Math.min(firstTokens.length, secondTokens.length);

  return firstTokens.slice(0, sharedLength).every((token, ix) => token === secondTokens[ix]);
};

/**
 * Find path conflicts between the requested applied patches.
 *
 * @param variantInformation - The persisted named patch catalogue
 * @param patchNames - Patch names that should be applied together
 * @returns Every overlapping path pair from different patches
 */
export const findVariantPatchConflicts = (
  variantInformation: VariantInformation,
  patchNames: string[],
): VariantPatchConflict[] => {
  const conflicts: VariantPatchConflict[] = [];
  const selectedNames = [...new Set(patchNames)];

  selectedNames.forEach((firstName, firstIx) => {
    const firstPatch = variantInformation.allVariantPatches[firstName];
    selectedNames.slice(firstIx + 1).forEach((secondName) => {
      const secondPatch = variantInformation.allVariantPatches[secondName];
      if (firstPatch && secondPatch) {
        firstPatch.forEach((firstOperation) => {
          secondPatch.forEach((secondOperation) => {
            if (pathsOverlap(firstOperation.path, secondOperation.path)) {
              conflicts.push({
                firstPatchName: firstName,
                firstPath: firstOperation.path,
                secondPatchName: secondName,
                secondPath: secondOperation.path,
              });
            }
          });
        });
      }
    });
  });

  return conflicts;
};
